<template>
  <ContactNative />

  <div v-if="isNoOrOfflineContent" :style="noContentZoneStyle" class="load-bar">
    <div v-if="newLoadStatus == LoadStatus.LOADING" class="sdk-load-loading">
      <!-- <div class="load-loading-icon"></div>
      <p class="load-loading-txt">正在加载...</p> -->
    </div>

    <div v-else :class="commentInvalidClass">
      <DynamicGraph :lottie-json="noContentLottie" :is-full-height="true" />
      <p class="load-txt">
        该评论已失效，不支持查看
      </p>
    </div>
  </div>

  <div v-else>
    <div :id="`feedback_${newComments[0].id}`" class="comment-detail">
      <ul
        v-for="itemData in newComments"
        :key="`comment_${itemData.id}`"
        class="comment-list"
      >
        <li
          v-if="itemData && itemData.owner"
          class="comment-item"
          @click.stop="
            onCommentClickThrottle(itemData, {
              directComment: true,
            })
          "
        >
          <div class="avatar-profile" @click.stop="directReport">
            <img
              class="avatar"
              :src="itemData.owner.avatarUrl"
              :onerror="onErrorAvatarDefault"
            >
          </div>

          <div class="comment-area">
            <div class="comment-top-info">
              <UserProfile
                :item-data="itemData"
                :parent-owner="itemData.owner"
                :show-tag-poster="true"
              />

              <PraiseButton
                :comment-id="itemData.id"
                :thumbup-count="itemData.thumbupCount"
                :is-thumb-up="itemData.isThumbUp"
                :comment-owner="itemData.owner"
              />
            </div>

            <ContentBody
              type="comment"
              :data="itemData"
              :is-second-comment-detail="true"
              @click="clickCommentContent(itemData)"
            />

            <MenuBar :item-data="itemData" :ui-reply="false" :is-show-title="true" />
          </div>
        </li>
      </ul>
    </div>

    <div v-if="newComments.length && !isFromMsgCenter">
      <CommentSecondDetailReply
        :comment-id="newComments[0].id"
        :parent-owner="newComments[0].owner"
        :replies="newComments[0].replies"
        :reply-size="newComments[0].replySize"
      />
    </div>

    <div v-else>
      <CommentSecondDetailReplyV2
        :comment-id="newComments[0].id"
        :parent-owner="newComments[0].owner"
        :replies="newComments[0].replies"
        :reply-size="newComments[0].replySize"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { onMounted, toRefs, defineComponent, watch, computed } from 'vue';
import { constant, utils } from '@cpc/comment-sdk-common';
import {
  useLaunchReplyPage,
  useCommentSecondDetail,
  useCommentListInject,
  useGlobalState,
} from '@cpc/comment-sdk-core-comment';
import wv from '@cpc/comment-sdk-jsapi';

import { useOnCommentClickInject } from '../core/useOnCommentClickProvide/index';
import { useBrowserVersion } from '../core/useBrowserVersion';
import { useStyleType } from '../core/useStyleType';
import { commonCommentStat } from '../core/useStat';
import { useMsgCenterSub } from '../core/useMsgCenterSub';
import { contentTypeMap } from '../assets/js/constant';
import avatarDefault from '../assets/img/avatar_default.png';
import avatarDefaultDark from '../assets/img/avatar_default_dark.png';
import noContent from '../assets/lottie/no-content-light.json?url';
import noContentDark from '../assets/lottie/no-content-dark.json?url';

import CommentSecondDetailReply from './CommentSecondDetailReply.vue';
import CommentSecondDetailReply_v2 from './CommentSecondDetailReply_v2.vue';
import PraiseButton from './PraiseButton.vue';
import ContentBody from './ContentBody.vue';
import MenuBar from './MenuBar.vue';
import UserProfile from './UserProfile.vue';
import ContactNative from './ContactNative.vue';
import DynamicGraph from './DynamicGraph.vue';

export default defineComponent({
  name: 'CommentSecondDetail',

  components: {
    PraiseButton,
    ContentBody,
    MenuBar,
    UserProfile,
    CommentSecondDetailReply,
    CommentSecondDetailReplyV2: CommentSecondDetailReply_v2,
    ContactNative,
    DynamicGraph,
  },

  setup(props) {
    const { LoadStatus } = constant;
    const { query } = utils;

    const { newComments, newLoadStatus } = useCommentListInject();
    const { fetchCommentDetail, setCommentCount } = useCommentSecondDetail();
    const { getIsDirectComment } = useLaunchReplyPage();
    const { onCommentClickThrottle } = useOnCommentClickInject();
    const { getColorOsVersion } = useBrowserVersion();
    const { isFromMsgCenter } = useMsgCenterSub();

    const { documentMeta, pageType, userMeta, isNightMode } = toRefs(useGlobalState());
    const uid = userMeta.value.uid;

    const stopWatch = watch(newComments.value, async (list) => {
      const isDirectComment = await getIsDirectComment();

      if (isDirectComment && list.length) {
        onCommentClickThrottle(list[0], {
          directComment: true,
        });
      }

      stopWatch();
    });

    const clickCommentContent = (itemData) => {
      commonCommentStat(20084688);

      onCommentClickThrottle(itemData, {
        directComment: true,
      });
    };

    const { itemData } = toRefs(props);

    const directReport = () => {
      commonCommentStat(20084690, {
        userId: itemData.value.owner.id,
      });

      if (uid === itemData.value.owner.id) {
        return;
      }

      onCommentClickThrottle(itemData.value, {
        directReport: true,
      });
    };

    onMounted(async () => {
      await fetchCommentDetail();

      setCommentCount();

      // 曝光埋点-二级回复页面
      const {
        fromId,
        mediaName,
        source,
        url,
        title,
        docId,
        styleType = '1',
      } = documentMeta.value;
      const { uid } = userMeta.value;

      const baseInfo = {
        category: contentTypeMap[styleType],
        fromId,
        title,
        url,
        source,
        sourceMedia: mediaName,
        iflowSource: 'OPPO',
        docId,
        userId: uid,
        commentPageType: pageType.value === 2 ? 'secondComment' : 'topComment',
      };
      const statParams = [20084004, 21006, 10012, JSON.stringify(baseInfo)];

      console.log('generalStat', ...statParams);
      wv.asyncInvoke('generalStat', ...statParams);
      document.documentElement.dataset.styling = getColorOsVersion();

      const { isNotVideo } = useStyleType();

      if (isNotVideo() && !isFromMsgCenter.value) {
        document.documentElement.dataset.styleType = 'not-video';
      }
    });

    const isNoContent = computed(() => {
      return newComments.value.length === 0;
    });

    const isOfflineContent = computed(() => {
      return newComments.value[0].status === -1;
    });

    const isNoOrOfflineContent = computed(() => {
      return isNoContent.value || isOfflineContent.value;
    });

    const noContentLottie = computed(() => {
      return isNightMode.value ? noContentDark : noContent;
    });

    const styling = computed(() => {
      return getColorOsVersion() === '13';
    });

    const commentInvalidClass = computed(() => {
      return styling.value ? 'comment-invalid' : '';
    });

    const noContentZoneStyle = computed(() => {
      return isNoOrOfflineContent.value
        ? {
            'height': `calc((${window.innerHeight}px) - 56px)`,
            'display': 'flex',
            'justify-content': 'center',
            'flex-direction': 'column',
          }
        : {};
    });

    const firstDetailComment = computed(() => {
      return newComments.value[0];
    });

    const onErrorAvatarDefault = (event) => {
      event.target.src = isNightMode.value ? avatarDefaultDark : avatarDefault;
    };

    return {
      newLoadStatus,
      newComments,
      onCommentClickThrottle,

      noContentZoneStyle,
      isNoOrOfflineContent,
      styling,

      isFromMsgCenter,
      firstDetailComment,

      noContentLottie,
      commentInvalidClass,

      LoadStatus,
      onErrorAvatarDefault,
      directReport,
      clickCommentContent,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "../assets/css/common.scss";
@import "../assets/css/loading.scss";
@import "../assets/css/load-bar.scss";
@import "../assets/css/styling-mixin.scss";

.comment-invalid {
  position: absolute;
  top: 45%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
}

.comment-detail {
  margin-bottom: 27px;
  padding: 21px 48px;
}

.comment-list {
  list-style: none;
  margin: 0;
  border: 0;
  padding: 0;
}

.comment-item {
  display: flex;
  color: #000;
}

.avatar-profile {
  width: 108px;
  height: 108px;
  border-radius: 54px;
  position: relative;
  padding-top: 15px;
  &:active {
    transform: scale(0.8);
    transition: all 0.3s ease;
  }
  @include setRounded1pxBorder(rgba(0, 0, 0, 0.1), before, 54px);

  .avatar {
    width: 108px;
    height: 108px;
    border-radius: 54px;
    margin-top: 0;
    vertical-align: top;
    border: 0;
  }
}

.comment-area {
  flex: 1;
  margin-left: 24px;
  font-size: 36px;
  font-family: ColorFont-Regular, "OPlusSans 3.0";
  min-width: 0; // fix flex & ellipsis
}

.comment-top-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 6px;
}

.comment-nick {
  height: 60px;
  font-weight: 500;
  font-size: 42px;
  color: rgba($color: #000, $alpha: 0.55);
}
</style>
